import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, CheckCircle2, XCircle, Clock } from "lucide-react";
import API, { formatError } from "../lib/api";

const STATUS_CONFIG = {
  verified:   { label: "Verified",       color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  partial:    { label: "Partially Verified", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  unverified: { label: "Not Verified",   color: "#6b7280", bg: "rgba(107,114,128,0.12)" },
};

function formatTime(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function SkillTest() {
  const { skill } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [phase, setPhase] = useState("loading"); // loading | testing | results | error
  const [questions, setQuestions] = useState([]);
  const [skillKey, setSkillKey] = useState("");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(600);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // theme tokens
  const bg         = isDark ? "#0a0a14"           : "#f8fafc";
  const surface    = isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.9)";
  const border     = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const textPrimary= isDark ? "#ffffff"            : "#0a0a14";
  const textMuted  = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const accent     = "#059669";
  const accentLight= isDark ? "rgba(5,150,105,0.12)" : "rgba(5,150,105,0.1)";

  useEffect(() => {
    API.get(`/api/skill-test/${encodeURIComponent(skill)}`)
      .then(res => {
        setQuestions(res.data.questions);
        setSkillKey(res.data.skill_key);
        setTimeLeft(res.data.duration_seconds || 600);
        setPhase("testing");
      })
      .catch(err => {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 429 && typeof detail === "object" && detail?.seconds_remaining) {
          const hrs = Math.ceil(detail.seconds_remaining / 3600);
          setErrorMsg(`You already took this test. You can retake it in ${hrs} hour${hrs !== 1 ? "s" : ""}.`);
        } else {
          setErrorMsg(formatError(err));
        }
        setPhase("error");
      });
  }, [skill]);

  const handleSubmit = useCallback(async (isTimeout = false) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await API.post("/api/skill-test/submit", { skill_name: skill, answers });
      setResult(res.data);
      setPhase("results");
    } catch (err) {
      setErrorMsg(formatError(err));
      setPhase("error");
    } finally {
      setSubmitting(false);
    }
  }, [skill, answers, submitting]);

  // Countdown timer
  useEffect(() => {
    if (phase !== "testing") return;
    if (timeLeft <= 0) {
      handleSubmit(true);
      return;
    }
    const id = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(id);
  }, [phase, timeLeft, handleSubmit]);

  const selectOption = (qId, opt) => setAnswers(prev => ({ ...prev, [qId]: opt }));

  const currentQ = questions[currentIdx];
  const answered = Object.keys(answers).length;
  const progress = questions.length > 0 ? (currentIdx / questions.length) * 100 : 0;
  const timerWarn = timeLeft < 60;

  if (phase === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: bg }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: accent, borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: bg }}>
        <Card style={{ background: surface, border: `1px solid ${border}`, maxWidth: 420, width: "100%" }}>
          <CardContent className="p-8 text-center">
            <XCircle size={40} className="mx-auto mb-4" style={{ color: "#ef4444" }} />
            <h2 className="text-lg font-semibold mb-2" style={{ color: textPrimary }}>Couldn't load test</h2>
            <p className="text-sm mb-6" style={{ color: textMuted }}>{errorMsg}</p>
            <Button onClick={() => navigate("/student/dashboard")} style={{ background: `linear-gradient(135deg, ${accent}, #047857)`, color: "white" }}>
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === "results") {
    const cfg = STATUS_CONFIG[result?.status] || STATUS_CONFIG.unverified;
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: bg }}>
        <Card style={{ background: surface, border: `1px solid ${border}`, maxWidth: 460, width: "100%" }}>
          <CardContent className="p-8 text-center">
            <CheckCircle2 size={48} className="mx-auto mb-4" style={{ color: cfg.color }} />
            <h2 className="text-2xl font-bold mb-1" style={{ color: textPrimary }}>
              {result?.correct}/{result?.total} Correct
            </h2>
            <p className="text-4xl font-black mb-4" style={{ color: cfg.color }}>{result?.pct}%</p>
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-6"
              style={{ background: cfg.bg, color: cfg.color }}>
              {cfg.label}
            </span>
            <div className="p-4 rounded-xl mb-6 text-sm" style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", border: `1px solid ${border}`, color: textMuted }}>
              {result?.status === "verified"   && "Great work! This skill is now marked as verified on your profile."}
              {result?.status === "partial"    && "Good effort! This skill is partially verified. Study more to reach full verification."}
              {result?.status === "unverified" && "Keep practicing! You can retake this test in 24 hours."}
            </div>
            <Button onClick={() => navigate("/student/dashboard")} className="w-full font-semibold"
              style={{ background: `linear-gradient(135deg, ${accent}, #047857)`, color: "white" }}>
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Testing phase
  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto min-h-screen p-4 md:p-8" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999, overflowY: 'auto', background: bg }}>
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/student/dashboard")} className="flex items-center gap-1.5 text-sm" style={{ color: textMuted }}>
            <ArrowLeft size={16} /> Dashboard
          </button>
          <div className="flex items-center gap-2 text-sm font-mono font-semibold px-3 py-1 rounded-lg"
            style={{ background: timerWarn ? "rgba(239,68,68,0.12)" : accentLight, color: timerWarn ? "#ef4444" : accent }}>
            <Clock size={14} />
            {formatTime(timeLeft)}
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-xs mb-2" style={{ color: textMuted }}>
            <span>Question {currentIdx + 1} of {questions.length}</span>
            <span>{answered} answered</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" }}>
            <div className="h-full rounded-full transition-all duration-300" style={{ width: `${progress}%`, background: `linear-gradient(90deg, ${accent}, #047857)` }} />
          </div>
        </div>

        {/* Question card */}
        <Card className="mb-4" style={{ background: surface, border: `1px solid ${border}` }}>
          <CardContent className="p-6">
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: accent }}>
              {skill.toUpperCase()} — Q{currentIdx + 1}
            </p>
            <h3 className="text-base font-medium leading-relaxed mb-6" style={{ color: textPrimary }}>
              {currentQ?.question}
            </h3>
            <div className="space-y-3">
              {currentQ && Object.entries(currentQ.options).map(([key, val]) => {
                const selected = answers[currentQ.id] === key;
                return (
                  <button
                    key={key}
                    onClick={() => selectOption(currentQ.id, key)}
                    className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150"
                    style={{
                      background: selected ? (isDark ? "rgba(5,150,105,0.18)" : "rgba(5,150,105,0.1)") : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
                      border: selected ? `2px solid ${accent}` : `2px solid ${border}`,
                      color: selected ? accent : textPrimary,
                    }}
                  >
                    <span className="font-bold mr-3" style={{ color: selected ? accent : textMuted }}>{key}.</span>
                    {val}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} disabled={currentIdx === 0}
            style={{ borderColor: border, color: textMuted, background: "transparent" }}>
            Previous
          </Button>

          {currentIdx < questions.length - 1 ? (
            <Button onClick={() => setCurrentIdx(i => i + 1)}
              style={{ background: `linear-gradient(135deg, ${accent}, #047857)`, color: "white" }}>
              Next
            </Button>
          ) : (
            <Button onClick={() => handleSubmit(false)} disabled={submitting}
              style={{ background: `linear-gradient(135deg, ${accent}, #047857)`, color: "white" }}>
              {submitting ? "Submitting…" : `Submit (${answered}/${questions.length} answered)`}
            </Button>
          )}
        </div>

        {/* Question dots */}
        <div className="flex flex-wrap gap-1.5 mt-5 justify-center">
          {questions.map((q, i) => (
            <button key={q.id} onClick={() => setCurrentIdx(i)}
              className="w-7 h-7 rounded-full text-xs font-semibold transition-all"
              style={{
                background: i === currentIdx ? accent : answers[q.id] ? accentLight : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
                color: i === currentIdx ? "white" : answers[q.id] ? accent : textMuted,
                border: i === currentIdx ? `2px solid ${accent}` : "2px solid transparent",
              }}>
              {i + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
